/**
 * ExperiencePricingPage
 *
 * Guide pricing management page for a specific experience.
 * Contains sections for seasonal pricing and price modifiers.
 *
 * Route: /guide/experiences/:experienceId/pricing
 *
 * SCRUM-1282 / SCRUM-1283 / SCRUM-1284 / SCRUM-1285 / SCRUM-1286 / SCRUM-1287 / SCRUM-1288
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, DollarSign } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SeasonalPricingSection from '../../components/guide/pricing/SeasonalPricingSection';
import PriceModifiersSection from '../../components/guide/pricing/PriceModifiersSection';
import ParticipantTypesSection from '../../components/guide/pricing/ParticipantTypesSection';
import AddonsPlaceholderSection from '../../components/guide/pricing/AddonsPlaceholderSection';
import PromotionCreationForm from '../../components/guide/pricing/PromotionCreationForm';
import PromotionListSection from '../../components/guide/pricing/PromotionListSection';
import PricingPreviewPanel from '../../components/guide/pricing/PricingPreviewPanel';
import PricingAnalyticsSection from '../../components/guide/pricing/PricingAnalyticsSection';
import { getPricingConfig } from '../../services/guidePricingApi';

export default function ExperiencePricingPage() {
  const { experienceId } = useParams();
  const navigate = useNavigate();
  const handleNavigate = (id, path) => { if (path) { navigate(path); } };
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadConfig() {
      try {
        setLoading(true);
        const res = await getPricingConfig(experienceId);
        setConfig(res.data);
      } catch (err) {
        setError(err.message || 'Failed to load pricing configuration');
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, [experienceId]);

  return (
    <DashboardLayout userRole="guide" activeNavId="experiences" onNavigate={handleNavigate}>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(`/guide/experiences/${experienceId}/edit`)}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-600" />
            <h1 className="text-xl font-bold text-gray-900">Pricing Management</h1>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg">
            {error}
          </div>
        ) : !config ? (
          <div className="bg-amber-50 text-amber-700 p-4 rounded-lg">
            <p className="font-medium">No pricing configuration found</p>
            <p className="text-sm mt-1">
              Set up base pricing in the experience editor first, then return here to configure seasonal pricing and modifiers.
            </p>
            <button
              onClick={() => navigate(`/guide/experiences/${experienceId}/edit`)}
              className="mt-3 text-sm text-amber-800 underline hover:no-underline"
            >
              Go to experience editor
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <SeasonalPricingSection experienceId={experienceId} />
            <PriceModifiersSection experienceId={experienceId} />
            <PromotionCreationForm experienceId={experienceId} />
            <PromotionListSection experienceId={experienceId} />
            <PricingPreviewPanel experienceId={experienceId} />
            <PricingAnalyticsSection experienceId={experienceId} />
            <ParticipantTypesSection experienceId={experienceId} />
            <AddonsPlaceholderSection />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
